import { Color, ExtrudeGeometry, Mesh, Object3D, Shape } from "three";
import { Jaco } from "oc-jaco3d/arrangement/Jaco";
import { Jaco3d } from "oc-jaco3d/arrangement3d/Jaco3d";
import { createUnitBoxMesh } from "../commons/Utils";
import { v3 } from "oc-threejs/short3";
import * as Scenes from "oc-threejs/Scenes";
import { setUnitBoxMeshFromOBB } from "oc-threejs/Scenes";
import * as Textures from "oc-threejs/Textures";
import { getWarnungWichtigkeitColor, mergeWarnungenByText, Warnung } from "../arrangement/Warnung";
import { WarnungZylinderSektor } from "../arrangement/Warnung";
import * as Dialogs from "oc-ui/dialogs/Dialogs";

export class Warnung3d extends Jaco3d {
  CLASS_NAME = "Warnung3d";
  private static readonly SEKTOR_VISUAL_SCALE = 1.01;
  private static readonly TORKONF_WARNUNG_OPACITY = 0.5;
  private static readonly TORKONF_WARNUNG_LABEL_PREFIX = "⚠ ";
  private static readonly TORKONF_WARNUNG_LABEL_TEXT_COLOR = "rgba(0, 0, 0, 0.95)";
  private static readonly TORKONF_WARNUNG_LABEL_BACKGROUND_COLOR = "rgba(255, 255, 255, 0.7)";
  declare jaco: Warnung;
  textplane: Object3D;
  private torkonfLabelCacheKey = "";

  emptySpaceVisualizerBox = null;
  sektorVisualizer: Mesh | null = null;
  sektorCacheKey = "";

  constructor(jaco: Jaco) {
    super(jaco);
    this.initVisualizer();
    this.renderOrder = -1;
  }

  private getCurrentWarnungColor() {
    const wichtigkeit = this.jaco.wichtigkeit ?? "hoch";
    return getWarnungWichtigkeitColor(wichtigkeit);
  }

  private setMeshColor(mesh: Mesh | null, colorHex: number) {
    if (!mesh) {
      return;
    }
    const materialList = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    materialList.forEach((material: any) => {
      if (!material) {
        return;
      }
      if (material.color instanceof Color) {
        material.color.setHex(colorHex);
        if (APP.isTorkonfigurator) {
          material.opacity = Warnung3d.TORKONF_WARNUNG_OPACITY;
        }
      }
      material.needsUpdate = true;
    });
  }

  private updateWarnungColor() {
    const { hex } = this.getCurrentWarnungColor();
    this.setMeshColor(this.emptySpaceVisualizerBox as Mesh, hex);
    this.setMeshColor(this.sektorVisualizer, hex);
  }

  private getTorkonfWarnungLabelIndex(): number | null {
    const text = this.jaco.text?.trim();
    if (!text) {
      return null;
    }

    const mergedWarnungen = mergeWarnungenByText(APP.arrangement?.warnungen ?? []);
    return mergedWarnungen.find((warnung) => warnung.text === text)?.index ?? null;
  }

  private removeTextplane() {
    Scenes.remove(this.textplane);
    this.textplane = null;
  }

  private createTorkonfWarnungLabel(labelText: string, prefixColor: string): Object3D {
    const commonLabelOptions = {
      supportMultiLine: true,
      cameraIndependendSize: false,
      orginalHeight: 0.17,
      lineHeightEm: 0.85,
    };

    const baseLabel = Textures.createTextSprite(labelText, {
      ...commonLabelOptions,
      color: Warnung3d.TORKONF_WARNUNG_LABEL_TEXT_COLOR,
      backgroundColor: Warnung3d.TORKONF_WARNUNG_LABEL_BACKGROUND_COLOR,
    });

    const prefixLabel = Textures.createTextSprite(labelText, {
      ...commonLabelOptions,
      color: prefixColor,
      backgroundColor: "transparent",
      textCanvasCallback: (ctx: CanvasRenderingContext2D) => {
        const lines = labelText.split(/\r?\n/);
        const firstLine = lines[0] ?? "";
        const firstLineWidth = ctx.measureText(firstLine).width;
        const prefixWidth = ctx.measureText(Warnung3d.TORKONF_WARNUNG_LABEL_PREFIX).width;
        const firstLineClipHeight = ctx.canvas.height / Math.max(1, lines.length);
        const x = (ctx.canvas.width - firstLineWidth) / 2;

        ctx.beginPath();
        ctx.rect(x - 1, 0, prefixWidth + 2, firstLineClipHeight);
        ctx.clip();
      },
    });

    prefixLabel.position.z = 0.001;

    const labelGroup = new Object3D();
    labelGroup.add(baseLabel);
    labelGroup.add(prefixLabel);
    return labelGroup;
  }

  private updateTorkonfWarnungLabel(color: string) {
    const index = this.getTorkonfWarnungLabelIndex();
    if (index == null) {
      this.removeTextplane();
      this.torkonfLabelCacheKey = "";
      return;
    }

    const labelText = `${Warnung3d.TORKONF_WARNUNG_LABEL_PREFIX}${index}\n↓`;
    const cacheKey = `${labelText}|${color}`;
    if (!this.textplane || this.torkonfLabelCacheKey !== cacheKey) {
      this.removeTextplane();
      this.textplane = this.createTorkonfWarnungLabel(labelText, color);
      this.add(this.textplane);
      this.torkonfLabelCacheKey = cacheKey;
    }

    this.textplane.hoverable = false;
    this.textplane.tapable = false;
    (this.textplane as any).receivesMouseRaycasts = false;
    this.textplane.onTap = null;

    const labelBox = this.jaco.getBox();
    const labelPos = labelBox.getCenter(v3());
    labelPos.y = labelBox.max.y;
    this.textplane.position.copy(labelPos).add(v3(0, 0.36 + (this.jaco.positionYOffset || 0), 0));
  }

  initVisualizer() {
    const baseMaterial = APP.materials.create("Warnung");
    const material = baseMaterial?.clone?.() ?? baseMaterial;
    if (APP.isTorkonfigurator) {
      material.opacity = Warnung3d.TORKONF_WARNUNG_OPACITY;
    }
    this.emptySpaceVisualizerBox = createUnitBoxMesh(material, "warnungVisualizerBox");
    this.add(this.emptySpaceVisualizerBox);
    this.updateWarnungColor();
  }

  update3d() {
    super.update3d();
    // this.traverse((o) => (o.renderOrder = -1));
    this.updateWarnungColor();
    const box3OrOBB = this.jaco.box3OrOBB;
    this.updateVisualizer(box3OrOBB);
    this.visible = APP.arrangement.zauneditorReady;

    const hasDialogConfig = this.jaco.dialogConfig != null;
    const warnungColor = this.getCurrentWarnungColor();

    if (APP.isTorkonfigurator) {
      this.updateTorkonfWarnungLabel(warnungColor.text);
      return;
    }

    if (this.torkonfLabelCacheKey) {
      this.removeTextplane();
      this.torkonfLabelCacheKey = "";
    }

    if (this.changed("text") || this.changed("wichtigkeit") || !this.textplane) {
      this.removeTextplane();
      this.textplane = Textures.createTextSprite((hasDialogConfig ? "ⓘ " : "") + this.jaco.text, {
        supportMultiLine: true,
        cameraIndependendSize: false,
        color: warnungColor.text,
        orginalHeight: 0.1,
        lineHeightEm: hasDialogConfig ? 1.3 : 1,
        textDecoration: hasDialogConfig ? "underline" : "none",
      });
      this.add(this.textplane);
    }

    if (!this.textplane) {
      return;
    }

    this.textplane.hoverable = hasDialogConfig;
    this.textplane.tapable = hasDialogConfig;
    (this.textplane as any).receivesMouseRaycasts = hasDialogConfig;
    this.textplane.intersectionPriority = 1;
    this.textplane.onTap = () => Dialogs.showWarningDetails(this.jaco.dialogConfig);

    const labelBox = this.jaco.getBox();
    const labelPos = labelBox.getCenter(v3());
    labelPos.y = labelBox.max.y;
    this.textplane.position.copy(labelPos).add(v3(0, 0.4 + (this.jaco.positionYOffset || 0), 0));
  }

  updateVisualizer(box3OrOBB: any) {
    if (box3OrOBB?.type === "zylindersektor") {
      this.updateSektorVisualizer(box3OrOBB as WarnungZylinderSektor);
      this.emptySpaceVisualizerBox.visible = false;
      return;
    }

    if (this.sektorVisualizer) {
      this.sektorVisualizer.visible = false;
    }
    this.emptySpaceVisualizerBox.visible = true;
    if (box3OrOBB.rotation) {
      // OBB
      setUnitBoxMeshFromOBB(this.emptySpaceVisualizerBox, box3OrOBB);
    } else {
      // Box3
      const center = box3OrOBB.getCenter(v3());
      this.emptySpaceVisualizerBox.position.copy(center);
    }
    this.emptySpaceVisualizerBox.scale.copy(box3OrOBB.getSize(v3()));
  }

  updateSektorVisualizer(sektor: WarnungZylinderSektor) {
    const visualScale = Warnung3d.SEKTOR_VISUAL_SCALE;
    const sweep = (() => {
      let s = sektor.endAngle - sektor.startAngle;
      if (s < 0) s += Math.PI * 2;
      return Math.max(0.0001, s);
    })();
    const height = Math.max(0.0001, sektor.yMax - sektor.yMin) * visualScale;
    const key = [sektor.radiusInner, sektor.radiusOuter, height, sektor.startAngle, sweep]
      .map((v) => Math.round(v * 1000) / 1000)
      .join("|");

    if (!this.sektorVisualizer || this.sektorCacheKey !== key) {
      if (this.sektorVisualizer) {
        this.remove(this.sektorVisualizer);
      }

      const shape = new Shape();
      const start = sektor.startAngle;
      const end = start + sweep;
      const rIn = Math.max(0, sektor.radiusInner * visualScale);
      const rOut = Math.max(rIn + 0.0001, sektor.radiusOuter * visualScale);

      shape.moveTo(rOut * Math.cos(start), rOut * Math.sin(start));
      shape.absarc(0, 0, rOut, start, end, false);
      shape.lineTo(rIn * Math.cos(end), rIn * Math.sin(end));
      shape.absarc(0, 0, rIn, end, start, true);
      shape.closePath();

      const geometry = new ExtrudeGeometry(shape, {
        depth: height,
        bevelEnabled: false,
        curveSegments: 24,
        steps: 1,
      });
      geometry.rotateX(Math.PI / 2);
      geometry.translate(0, height, 0);

      const baseMaterial = APP.materials.create("Warnung");
      const material = baseMaterial?.clone?.() ?? baseMaterial;
      this.sektorVisualizer = new Mesh(geometry, material);
      this.sektorVisualizer.name = "warnungVisualizerSektor";
      this.sektorVisualizer.renderOrder = -1;
      this.add(this.sektorVisualizer);
      this.sektorCacheKey = key;
      this.updateWarnungColor();
    }

    this.sektorVisualizer.visible = true;
    this.sektorVisualizer.position.set(sektor.pivot.x, sektor.yMin, sektor.pivot.z);
  }

  // @ts-ignore
  clone(recursive: boolean) {
    return new Warnung3d(this.jaco);
  }

  getBox() {
    return this.jaco.getBox();
  }
}
